import type { WarehouseName } from '../../prototype/data/warehouse.ts'
import { type PurchaseRequestInput, proto } from '../../prototype/state/store.ts'
import {
  actionError,
  recordVersion,
  toastAction,
  uploadWarehouseFiles,
  writeWarehouse,
} from '../warehouse/actions.ts'

const versions = (number: string) =>
  Object.fromEntries(
    proto.state.expected
      .filter((row) => row.supplyOrder === number)
      .map((row) => [row.id, recordVersion(row)]),
  )
const orderPath = (number: string) => `/purchasing/orders/${encodeURIComponent(number)}`
const errorAction = async (operation: () => Promise<unknown>): Promise<string | null> => {
  try {
    await operation()
    return null
  } catch (error) {
    return actionError(error)
  }
}
export const purchasingActions = {
  requestPurchase: (input: PurchaseRequestInput) =>
    toastAction(async () => {
      await writeWarehouse('/purchasing/requests', input)
      return true
    }),
  cancelPurchaseRequest: (id: string) =>
    toastAction(async () => {
      await writeWarehouse(`/purchasing/requests/${encodeURIComponent(id)}/cancel`, {
        version: recordVersion(proto.state.purchaseRequests.find((row) => row.id === id)),
      })
      return true
    }),
  markPurchaseOrdered: (id: string) =>
    toastAction(async () => {
      await writeWarehouse(`/purchasing/requests/${encodeURIComponent(id)}/ordered`, {
        version: recordVersion(proto.state.purchaseRequests.find((row) => row.id === id)),
      })
      return true
    }),
  placeSupplyOrder: (input: {
    supplier: string
    buyerEntityId: string | null
    lines: { materialId: string; quantity: string; unitCost: string }[]
    warehouse: WarehouseName
    location: string | null
    expectedAt: string | null
    files?: File[]
    invoice: string | null
    requestIds: string[]
    payment: { dueAt: string } | null
  }) =>
    toastAction(async () => {
      const { files, ...data } = input
      const attachments = await uploadWarehouseFiles(files)
      return (
        await writeWarehouse<{ number: string }>('/purchasing/orders', { ...data, attachments })
      ).number
    }),
  updateSupplyOrder: (
    number: string,
    input: {
      supplier: string
      expectedAt: string | null
      warehouse: WarehouseName
      invoice: string | null
      files?: File[]
    },
    expectedVersions?: Record<string, number>,
  ) =>
    errorAction(async () => {
      const submittedVersions = expectedVersions ?? versions(number)
      const { files, ...data } = input
      const attachments = await uploadWarehouseFiles(files)
      return await writeWarehouse(
        orderPath(number),
        { ...data, attachments, versions: submittedVersions },
        'PATCH',
      )
    }),
  markSupplyInTransit: (number: string) =>
    toastAction(async () => {
      await writeWarehouse(`${orderPath(number)}/transit`, { versions: versions(number) })
      return true
    }),
  requestSupplyPayment: (
    number: string,
    input: { invoice: string; dueAt: string; buyerEntityId: string },
    expectedVersions?: Record<string, number>,
  ) =>
    errorAction(() =>
      writeWarehouse(`${orderPath(number)}/payment`, {
        ...input,
        versions: expectedVersions ?? versions(number),
      }),
    ),
}
